/**
 * The cookies product analytics keeps, for both hosts of the site at once
 * (`docs/product-analytics.md`, "One visitor on two hosts").
 *
 * The site is one bundle on two hosts (`surface.ts`), and `localStorage` belongs to one origin: a
 * choice made on the library was not a choice on the creator dashboard, and one person reading
 * both was two visitors in two sessions. So the choice, the random visitor id and the usage
 * session live in cookies for the parent domain, which both hosts read — the same parent the
 * sign-in mark is written for (`markerDomain` in `api.ts`). On `localhost` a cookie ignores the
 * port, which is the same thing; on an address or a bare name there is no parent and the cookie is
 * this host's alone.
 *
 * **None of them is a credential, and none ever holds an account id.** They are a word (`off` or
 * `on`), a random UUID and a random UUIDv7 with two timestamps. A page reads them; nothing on the
 * API's side does.
 *
 * Best effort. A browser that will not keep one — cookies blocked, a host whose parent is a public
 * suffix, a frame — is told so by `writeAnalyticsCookie`, and the caller falls back to this
 * origin's `localStorage`, which is how all three were kept before.
 */

export const YEAR_SECONDS = 365 * 24 * 60 * 60;

function parentDomain(host: string): string {
  // An address or a bare name has no parent to share with.
  if (host === 'localhost' || /^[\d.]+$/.test(host) || host.includes(':') || !host.includes('.')) return '';
  return `; Domain=${host.split('.').slice(-2).join('.')}`;
}

export function readAnalyticsCookie(name: string): string | null {
  try {
    for (const part of String(document.cookie ?? '').split(';')) {
      const at = part.indexOf('=');
      if (at > 0 && part.slice(0, at).trim() === name) return part.slice(at + 1).trim();
    }
  } catch {
    // No document, or a frame that may not read cookies.
  }
  return null;
}

/**
 * Keep `value` for `maxAge` seconds — or, with zero, take it away. True only when the browser did
 * as asked, read back to be sure: `document.cookie` refuses silently.
 */
export function writeAnalyticsCookie(name: string, value: string, maxAge: number): boolean {
  try {
    const { protocol, hostname } = window.location;
    const secure = protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${name}=${maxAge > 0 ? value : ''}; Path=/; Max-Age=${Math.max(0, Math.floor(maxAge))}; SameSite=Lax${secure}${parentDomain(hostname)}`;
    return readAnalyticsCookie(name) === (maxAge > 0 ? value : null);
  } catch {
    return false;
  }
}

export function dropAnalyticsCookie(name: string): void {
  writeAnalyticsCookie(name, '', 0);
}
